import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { careerType } from 'services/careerType';

import { DIM_OPACITY, buildEdgePaths } from '../model/skillGraphEdges';
import {
  CARD_PAD_X,
  CARD_RX,
  CLUSTER_CLIP_W,
  CLUSTER_H,
  GLYPH_SIZE,
  ISkillLayout,
  LINE_BASELINE,
  META_BASELINE,
  MORE_CLIP_W,
  MORE_H,
  NAME_CLIP_W,
  NODE_H,
  RING_CX,
  RING_GLYPH,
  RING_R,
  TEXT_X,
  TITLE_BASELINE,
  TRACK_CLIP_W,
  TRACK_H,
} from '../model/skillGraphLayout';
import { clusterProgressText, ISkillGraphIndex } from '../model/skillGraphModel';
import { levelLine, levelPhrase } from '../model/skillLevelLabel';
import { ArrowKey, nextFocus } from '../model/skillGraphNav';
import { IViewport } from '../model/viewportMath';
import {
  SKILL_STATUS_LOOK,
  STATUS_TINT,
  STATUS_TINT_DARK,
  STATUS_TINT_LIGHT,
  tintVars,
} from '../skillStatus';
import { FALLBACK_CLUSTER_ICON, resolveClusterIcon } from '../model/clusterIcon';

import { ClusterProgressRing } from './ClusterProgressRing';
import { SkillGlyph } from './SkillGlyph';
import { TrackGlyph } from './TrackGlyph';

/** Сколько пикселей края уходит в прозрачность у обрезанного имени. */
const FADE_W = 12;

/**
 * Холст карты навыков (ТЗ 0070).
 *
 * Собственный SVG, без граф-библиотеки. Раскладку считает чистая функция
 * (model/skillGraphLayout), поэтому приёмочное требование «одинаковые данные
 * дают одинаковые координаты» держится по построению, а не по свойству чужого
 * кода, который мы не проверяем.
 *
 * 🔴 Панорама и масштаб живут в РЕФЕ и пишутся в transform группы внутри
 * requestAnimationFrame. Держать их в состоянии React значит перерисовывать
 * шестьдесят узлов шестьдесят раз в секунду ради перетаскивания мышью.
 *
 * 🔴 Фокус — aria-activedescendant, а НЕ tabIndex на каждом узле. При
 * шестидесяти узлах Tab иначе становится шестьюдесятью остановками, и уйти с
 * карты клавиатурой невозможно. Здесь Tab входит в карту одним нажатием и
 * выходит одним.
 *
 * ⚠️ Выбранный узел помечен aria-pressed, а не aria-selected: роль button
 * второго не поддерживает вовсе, и скринридер его молча игнорирует.
 */

interface ISkillGraphCanvas {
  index: ISkillGraphIndex;
  layout: ISkillLayout;
  viewport: IViewport;
  selected: string | null;
  focused: string | null;
  deps: boolean;
  highlighted: Set<string>;
  onSelect: (nodeId: string) => void;
  onFocusChange: (key: string | null) => void;
  onZoomCards: (key: string) => void;
  onPan: (dx: number, dy: number) => void;
  onWheelZoom: (factor: number, point: { x: number; y: number }) => void;
}

export const SkillGraphCanvas = memo(
  ({
    index,
    layout,
    viewport,
    selected,
    focused,
    deps,
    highlighted,
    onSelect,
    onFocusChange,
    onZoomCards,
    onPan,
    onWheelZoom,
  }: ISkillGraphCanvas) => {
    const { t } = useTranslation();
    const svgRef = useRef<SVGSVGElement>(null);
    const sceneRef = useRef<SVGGElement>(null);

    // Трансформация пишется мимо React: это единственное, что двигается на
    // каждом кадре перетаскивания.
    useEffect(() => {
      const frame = requestAnimationFrame(() => {
        if (!sceneRef.current) return;
        sceneRef.current.setAttribute(
          'transform',
          `translate(${viewport.x} ${viewport.y}) scale(${viewport.zoom})`,
        );
      });
      return () => cancelAnimationFrame(frame);
    }, [viewport]);

    const edges = useMemo(
      () => buildEdgePaths(index, layout, { deps, selected }),
      [index, layout, deps, selected],
    );

    /**
     * Ключ значка на каждую карточку группы, посчитанный ОДИН раз на карту.
     * Карточка знает только свой ключ вида `cluster:<код>`, а значок живёт в
     * самой группе — искать её линейно на каждой из шестидесяти отрисовок
     * незачем.
     */
    const clusterIcons = useMemo(
      () =>
        new Map(
          index.clusters.map((cluster) => [`cluster:${cluster.code}`, resolveClusterIcon(cluster)]),
        ),
      [index.clusters],
    );

    /**
     * Колесо ведёт себя как в любом просмотрщике: обычная прокрутка двигает
     * сцену, с Ctrl (и жест «щипок» на трекпаде, который браузер отдаёт тем же
     * событием) — меняет масштаб.
     *
     * ⚠️ Слушатель вешается вручную с passive: false. React вешает onWheel
     * пассивным, и preventDefault в нём молча игнорируется — страница
     * прокручивалась бы вместе с картой.
     */
    useEffect(() => {
      const node = svgRef.current;
      if (!node) return undefined;
      const onWheel = (event: WheelEvent) => {
        event.preventDefault();
        if (event.ctrlKey || event.metaKey) {
          const box = node.getBoundingClientRect();
          onWheelZoom(event.deltaY < 0 ? 1.1 : 1 / 1.1, {
            x: event.clientX - box.left,
            y: event.clientY - box.top,
          });
          return;
        }
        onPan(-event.deltaX, -event.deltaY);
      };
      node.addEventListener('wheel', onWheel, { passive: false });
      return () => node.removeEventListener('wheel', onWheel);
    }, [onPan, onWheelZoom]);

    /**
     * Перетаскивание. Указатель захватывается, чтобы курсор, ушедший за край
     * карты, не «ронял» жест на середине.
     *
     * 🔴 Захват включается ТОЛЬКО после того, как палец реально сдвинулся на
     * DRAG_THRESHOLD. Захват на самом `pointerdown` уводил все дальнейшие
     * события на `<svg>`, и браузер выдавал `click` тоже на нём — а обработчик
     * выбора висит на группе узла. Экран выглядел исправным (узлы
     * подсвечивались, карта таскалась), но ВЫБРАТЬ навык мышью было нельзя:
     * ни паспорта, ни `?skill=` в адресе. Поймано глазами; ни tsc, ни
     * рендер-тесты этого не видят — они кликают программно, а
     * `dispatchEvent('click')` захвата указателя не касается вовсе.
     */
    const DRAG_THRESHOLD = 3;
    const dragFrom = useRef<{ x: number; y: number } | null>(null);
    const dragging = useRef(false);
    const onPointerDown = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
      if (event.button !== 0) return;
      dragFrom.current = { x: event.clientX, y: event.clientY };
      dragging.current = false;
    }, []);
    const onPointerMove = useCallback(
      (event: React.PointerEvent<SVGSVGElement>) => {
        const from = dragFrom.current;
        if (!from) return;
        const [dx, dy] = [event.clientX - from.x, event.clientY - from.y];
        if (!dragging.current) {
          if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
          dragging.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
        }
        onPan(dx, dy);
        dragFrom.current = { x: event.clientX, y: event.clientY };
      },
      [onPan],
    );
    const onPointerUp = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
      dragFrom.current = null;
      dragging.current = false;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }, []);

    const onKeyDown = useCallback(
      (event: React.KeyboardEvent<SVGSVGElement>) => {
        if (event.key === 'Escape') {
          // Escape уже забрало меню настроек поверх карты.
          if (event.isDefaultPrevented()) return;
          // 🔴 `preventDefault` — знак паспорту: Escape уже обработан картой.
          // Паспорт слушает Escape на документе, и без знака одно нажатие
          // закрывало бы его дважды — двумя записями в истории.
          event.preventDefault();
          onSelect('');
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (focused) onSelect(focused);
          return;
        }
        const arrows: ArrowKey[] = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
        if (!arrows.includes(event.key as ArrowKey)) return;
        event.preventDefault();
        const next = nextFocus(index, layout, focused, event.key as ArrowKey, { deps });
        if (next) onFocusChange(next);
      },
      [index, layout, focused, deps, onSelect, onFocusChange],
    );

    return (
      <Svg
        ref={svgRef}
        role="application"
        aria-roledescription={t('careerCenter.map.canvasRole')}
        aria-label={t('careerCenter.map.canvasLabel')}
        aria-activedescendant={focused ? `skm-${focused}` : undefined}
        aria-describedby="skm-help"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}>
        <defs>
          <marker
            id="skm-arrow"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="7"
            markerHeight="7"
            orient="auto">
            <path d="M 0 0 L 8 4 L 0 8 z" fill={Colors.gray_dark} />
          </marker>
          {/* 🔴 Обрезка подписей. У SVG-текста её нет: `text-overflow` к
              `<text>` не применяется, и длинное имя молча рисуется поверх
              рамки — сегодня это ловится только глазами и только если знать,
              куда смотреть. Резать по числу символов нельзя, это догадка о
              пикселях; клип — гарантия при любом шрифте, и он же снимает
              запас «на подмену шрифта» из вывода ширины карточки.

              Край гасится маской-градиентом на последних 12 px: буква уходит
              в прозрачность, и это читается как «дальше есть ещё», не требуя
              измерять текст. Полное имя живёт в паспорте, в списке и в
              доступном имени карточки. */}
          <clipPath id="skm-clip-name">
            <rect x={0} y={0} width={NAME_CLIP_W} height={NODE_H} />
          </clipPath>
          <clipPath id="skm-clip-cluster">
            <rect x={0} y={0} width={CLUSTER_CLIP_W} height={CLUSTER_H} />
          </clipPath>
          <clipPath id="skm-clip-track">
            <rect x={0} y={0} width={TRACK_CLIP_W} height={TRACK_H} />
          </clipPath>
          <clipPath id="skm-clip-more">
            <rect x={0} y={0} width={MORE_CLIP_W} height={MORE_H} />
          </clipPath>
          <linearGradient id="skm-fade-name" x1="0" x2="1" y1="0" y2="0">
            <stop offset={(NAME_CLIP_W - FADE_W) / NAME_CLIP_W} stopColor="#fff" />
            <stop offset="1" stopColor="#000" />
          </linearGradient>
          <mask id="skm-mask-name">
            <rect x={0} y={0} width={NAME_CLIP_W} height={NODE_H} fill="url(#skm-fade-name)" />
          </mask>
        </defs>
        <g ref={sceneRef}>
          {/* Слой связей ПОД узлами: линия не имеет права перечеркнуть
              подпись, которую она соединяет. */}
          <g>
            {edges.hierarchy.map((edge) => (
              <path
                key={edge.key}
                d={edge.d}
                fill="none"
                stroke={Colors.system_gray}
                strokeWidth={2}
              />
            ))}
            {edges.dependencies.map((edge) => (
              <path
                key={edge.key}
                d={edge.d}
                fill="none"
                stroke={Colors.gray_dark}
                strokeWidth={2}
                strokeDasharray={edge.kind === 'recommended' ? '8 6' : undefined}
                markerEnd="url(#skm-arrow)"
                opacity={edge.dimmed ? DIM_OPACITY : 0.75}
              />
            ))}
          </g>

          {layout.cards.map((card) => {
            if (card.kind === 'more') {
              return (
                <g
                  key={card.key}
                  id={`skm-${card.key}`}
                  role="button"
                  tabIndex={-1}
                  aria-label={t('careerCenter.map.more', { n: card.hidden.length })}
                  transform={`translate(${card.x} ${card.y})`}
                  onClick={() => onZoomCards(card.key)}
                  style={{ cursor: 'pointer' }}>
                  <rect
                    width={card.w}
                    height={card.h}
                    rx={CARD_RX}
                    fill={Colors.bg_gray}
                    stroke={Colors.system_gray}
                    strokeDasharray="5 5"
                  />
                  {/* Кегль метаданных, а не имени: «Ещё 12 навыков» — это
                      счётчик, а не название. Заодно подпись перестаёт впритык
                      упираться в рамку: 158 px против 188 доступных. */}
                  <g clipPath="url(#skm-clip-more)">
                    <MetaText x={CARD_PAD_X} y={LINE_BASELINE}>
                      {t('careerCenter.map.more', { n: card.hidden.length })}
                    </MetaText>
                  </g>
                </g>
              );
            }

            const look = card.node ? SKILL_STATUS_LOOK[card.node.status] : null;
            const isSelected = card.key === selected;
            const isFocused = card.key === focused;
            const dimmed =
              highlighted.size > 0 && !highlighted.has(card.key) && card.kind === 'skill';

            return (
              <g
                key={card.key}
                id={`skm-${card.key}`}
                role="button"
                aria-label={
                  card.node && look
                    ? t('careerCenter.map.nodeLabel', {
                        name: card.node.name,
                        status: t(look.labelKey),
                        levels: t(levelPhrase(card.node).key, levelPhrase(card.node).vars),
                      })
                    : card.label
                }
                aria-pressed={isSelected}
                /* 🔴 ТЗ 0071, п. 6.4: после закрытия паспорта фокус
                    возвращается на узел. Узел для этого обязан уметь принять
                    фокус (tabIndex −1 — не остановка табуляции), но держать его
                    нельзя: карта живёт на aria-activedescendant, и стрелки
                    слушает сам svg. Поэтому фокус тут же переезжает на svg, а
                    узел становится его активным потомком. */
                tabIndex={card.kind === 'skill' ? -1 : undefined}
                onFocus={
                  card.kind === 'skill'
                    ? () => {
                        onFocusChange(card.key);
                        svgRef.current?.focus({ preventScroll: true });
                      }
                    : undefined
                }
                transform={`translate(${card.x} ${card.y})`}
                opacity={dimmed ? 0.35 : 1}
                onClick={() => (card.kind === 'skill' ? onSelect(card.key) : onZoomCards(card.key))}
                onDoubleClick={() => onZoomCards(card.key)}
                style={{ cursor: 'pointer' }}>
                <rect
                  width={card.w}
                  height={card.h}
                  rx={CARD_RX}
                  fill={card.node ? STATUS_TINT[card.node.status] : Colors.white}
                  stroke={look ? look.accent : Colors.black}
                  strokeWidth={card.kind === 'track' ? 2.5 : 2}
                  strokeDasharray={
                    look?.border === 'dashed'
                      ? '9 6'
                      : look?.border === 'dotted'
                      ? '3 5'
                      : undefined
                  }
                />
                {look?.border === 'double' && (
                  <rect
                    x={4}
                    y={4}
                    width={card.w - 8}
                    height={card.h - 8}
                    rx={8}
                    fill="none"
                    stroke={look.accent}
                    strokeWidth={1}
                  />
                )}
                {/* 🔴 Кольцо фокуса рисуется ВТОРЫМ прямоугольником внутри SVG:
                    outline на <g> не рисуется вовсе, и клавиатурный фокус
                    остался бы невидимым. */}
                {isFocused && (
                  <rect
                    x={-3}
                    y={-3}
                    width={card.w + 6}
                    height={card.h + 6}
                    rx={CARD_RX + 3}
                    fill="none"
                    stroke={Colors.black}
                    strokeWidth={2}
                  />
                )}
                {card.node && (
                  <SkillGlyph
                    x={CARD_PAD_X}
                    y={(card.h - GLYPH_SIZE) / 2}
                    size={GLYPH_SIZE}
                    slug={card.node.slug}
                    name={card.node.name}
                    iconUrl={card.node.iconUrl}
                    category={card.node.category}
                  />
                )}
                {/* 🔴 У колонки — кольцо доли, а не цветная точка. Точка ничего
                    не сообщала: доля уже написана словами ниже, и раскрасить её
                    было нечем — жёлтый `main` на светлой карточке даёт 1.4:1.
                    Кольцо показывает ту же дробь фигурой, и подпись остаётся
                    на месте. */}
                {/* 🔴 Знак профессии на той же левой полке, что и знак
                    технологии у навыка (16 + 36): три вида карточек получают
                    одну полку, и холст читается одним взглядом, а не тремя
                    разными. */}
                {card.kind === 'track' && (
                  <TrackGlyph
                    x={CARD_PAD_X}
                    y={(card.h - GLYPH_SIZE) / 2}
                    size={GLYPH_SIZE}
                    icon={index.trackIcon}
                    disc
                  />
                )}
                {card.kind === 'cluster' && (
                  <>
                    <ClusterProgressRing
                      cx={RING_CX}
                      cy={card.h / 2}
                      r={RING_R}
                      done={card.done}
                      total={card.total}
                    />
                    {/* Знак группы стоит ВНУТРИ кольца: кольцо говорит о доле
                        закрытого, знак — о том, что это за группа. То же
                        правило, что на карточках профессий в каталоге. Дырка
                        кольца здесь свободна — дробь «5 из 12» вынесена
                        подписью ниже. */}
                    <TrackGlyph
                      x={RING_CX - RING_GLYPH / 2}
                      y={card.h / 2 - RING_GLYPH / 2}
                      size={RING_GLYPH}
                      icon={clusterIcons.get(card.key) || FALLBACK_CLUSTER_ICON}
                    />
                  </>
                )}
                {/* 🔴 Одна левая полка на ВСЕ виды карточек и одна базовая
                    линия на все ОДНОСТРОЧНЫЕ. Двухстрочной осталась только
                    группа: её заголовок в одну строку потребовал бы карточку
                    шире нынешней. Числа выведены в `skillGraphLayout`.

                    Имя обрезается клипом: у SVG-текста своей обрезки нет, и
                    длинное имя иначе рисуется поверх рамки. */}
                <g
                  clipPath={
                    card.kind === 'cluster'
                      ? 'url(#skm-clip-cluster)'
                      : card.kind === 'track'
                      ? 'url(#skm-clip-track)'
                      : 'url(#skm-clip-name)'
                  }
                  mask={card.kind === 'skill' ? 'url(#skm-mask-name)' : undefined}>
                  <CardText
                    x={TEXT_X}
                    y={
                      card.kind === 'cluster' || card.kind === 'skill'
                        ? TITLE_BASELINE
                        : LINE_BASELINE
                    }>
                    {card.label}
                  </CardText>
                  {/* 🔴 ТЗ 0071: вторая строка — статус словами вместо дроби
                      «5 / 3», которую проверка в Brave признала нечитаемой.
                      Лежит под тем же клипом и маской, что имя: русская фраза
                      длиннее польской, и за рамку она уйти не должна. Полная
                      фраза уровня — в доступном имени, списке и паспорте. */}
                  {card.kind === 'skill' && card.node && (
                    <MetaText x={TEXT_X} y={META_BASELINE}>
                      {t(levelLine(card.node).key, levelLine(card.node).vars)}
                    </MetaText>
                  )}
                </g>
                {card.kind === 'cluster' && (
                  <MetaText x={TEXT_X} y={META_BASELINE}>
                    {t(clusterProgressText(card).key, clusterProgressText(card).vars)}
                  </MetaText>
                )}
              </g>
            );
          })}
        </g>
      </Svg>
    );
  },
);

/*
 * 🔴 Переменные заливки объявлены на САМОМ svg, а не в глобальной теме.
 * `fill` в SVG читает `var(...)` по наследованию, поэтому семи статусам
 * достаточно одного места — и это место рядом с картой, а не в палитре всего
 * приложения, где такому набору взяться неоткуда. Значения печатаются из
 * `skillStatus`, руками их здесь не переписывают.
 */
const Svg = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
  /* 🔴 Без этого перетаскивание карты ВЫДЕЛЯЕТ подписи: холст остаётся на
     месте, а по нему тянется синяя полоса выделения, и жест выглядит
     сломанным. pointer-events: none у самих подписей от этого не спасает —
     выделение ведёт браузер, а не они. Поймано глазами. */
  user-select: none;
  background: ${Colors.white};
  ${tintVars(STATUS_TINT_LIGHT)}

  [data-theme='dark'] & {
    ${tintVars(STATUS_TINT_DARK)}
  }

  &:focus-visible {
    outline: 2px solid ${Colors.black};
    outline-offset: -2px;
  }
`;

/* ⚠️ Подписи НЕ измеряются: раскладка, зависящая от того, какие шрифты успели
   загрузиться, перестаёт быть воспроизводимой. Ширина карточки выведена из
   самой длинной подписи арифметикой — см. комментарий к NODE_W. Обрезки у
   SVG-текста нет (`text-overflow` к `<text>` не применяется), поэтому запас
   ширины там и заложен. */
const CardText = styled.text`
  ${careerType.cardTitle()};
  fill: ${Colors.black};
  pointer-events: none;
`;

const MetaText = styled.text`
  ${careerType.meta()};
  fill: ${Colors.gray_dark};
  pointer-events: none;
`;
